import { Plus, Search, Star } from "lucide-react";
import { useState, type JSX } from "react";

import type { ChannelDTO, MemberDTO } from "../../../common/api/types.js";
import { Avatar } from "../../../common/components/Avatar.js";
import { SidebarItem, SidebarSection } from "../../../common/components/SidebarNav.js";
import { useMembers } from "../../../common/people/peopleQueries.js";
import type { ModulePageProps } from "../../ModuleDefinition.js";
import { useChatLocation } from "../chatLocation.js";
import { useChannels, useOpenDirect, useSetChannelFavorite } from "../query/chatQueries.js";
import { NewChatDialog } from "./NewChatDialog.js";

/** Sidebar der Chat-App: laufende Unterhaltungen und wen man sonst noch ansprechen kann. */
export function ChatSidebar({ me }: ModulePageProps): JSX.Element {
  const location = useChatLocation();
  const channels = useChannels();
  const members = useMembers("");
  const openDirect = useOpenDirect();
  const setFavorite = useSetChannelFavorite();
  const [filter, setFilter] = useState("");
  const [newChatOpen, setNewChatOpen] = useState(false);

  const channelList = channels.data ?? [];
  const normalizedFilter = filter.trim().toLocaleLowerCase("de-DE");
  const matches = (value: string): boolean =>
    normalizedFilter.length === 0 || value.toLocaleLowerCase("de-DE").includes(normalizedFilter);
  const visibleChannels = channelList.filter((channel) =>
    matches(`${channel.title} ${channel.lastMessagePreview ?? ""}`),
  );
  const favorites = visibleChannels.filter((channel) => channel.favorite);
  const recent = visibleChannels.filter((channel) => !channel.favorite);
  const directByUser = new Map(
    channelList.flatMap((channel) =>
      channel.kind === "DIRECT"
        ? channel.members
            .filter((member) => member.userId !== me.user.id)
            .map((member) => [member.userId, channel] as const)
        : [],
    ),
  );
  const people = (members.data ?? []).filter(
    (member) =>
      member.userId !== me.user.id &&
      member.role !== "GUEST" &&
      matches(`${member.displayName} ${member.email}`),
  );
  const guests = (members.data ?? []).filter(
    (member) =>
      member.userId !== me.user.id &&
      member.role === "GUEST" &&
      matches(`${member.displayName} ${member.email}`),
  );

  const openMember = (member: MemberDTO, closeDialog = false): void => {
    const existing = directByUser.get(member.userId);
    if (existing) {
      location.openChannel(existing.id);
      if (closeDialog) setNewChatOpen(false);
      return;
    }
    openDirect.mutate(member.userId, {
      onSuccess: (channel) => {
        location.openChannel(channel.id);
        if (closeDialog) setNewChatOpen(false);
      },
    });
  };

  const renderChannel = (channel: ChannelDTO): JSX.Element => {
    const other = channel.members.find((member) => member.userId !== me.user.id);
    return (
      <SidebarItem
        key={channel.id}
        label={channel.title}
        hint={channel.lastMessagePreview}
        icon={
          <Avatar
            displayName={other?.displayName ?? channel.title}
            avatarUrl={other?.avatarUrl}
            presence={other?.presence ?? null}
            size="sm"
          />
        }
        active={channel.id === location.channelId}
        badge={channel.unreadCount}
        action={
          <button
            type="button"
            aria-label={channel.favorite ? `${channel.title} aus Favoriten entfernen` : `${channel.title} favorisieren`}
            title={channel.favorite ? "Aus Favoriten entfernen" : "Favorisieren"}
            onClick={() => setFavorite.mutate({ channelId: channel.id, favorite: !channel.favorite })}
            className={[
              "rounded-md p-1.5 hover:bg-surface",
              channel.favorite ? "text-brand" : "text-ink-muted opacity-0 group-hover:opacity-100 focus:opacity-100",
            ].join(" ")}
          >
            <Star className="h-3.5 w-3.5" fill={channel.favorite ? "currentColor" : "none"} />
          </button>
        }
        onClick={() => location.openChannel(channel.id)}
      />
    );
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2 px-2">
        <h2 className="text-base font-semibold text-ink">Chat</h2>
        <button
          type="button"
          aria-label="Neue Unterhaltung"
          title="Neue Unterhaltung"
          onClick={() => setNewChatOpen(true)}
          className="rounded-lg bg-brand p-2 text-brand-ink hover:bg-brand/90"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <label className="relative mb-5 block">
        <span className="sr-only">Chats und Personen filtern</span>
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-ink-muted" />
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Chats und Personen suchen"
          className="w-full rounded-lg border border-line bg-surface-sunken py-2 pl-8 pr-3 text-sm text-ink outline-none focus:border-brand"
        />
      </label>

      {favorites.length > 0 ? (
        <SidebarSection title="Favoriten">{favorites.map(renderChannel)}</SidebarSection>
      ) : null}

      <SidebarSection title="Letzte Chats">
        {recent.length > 0 ? recent.map(renderChannel) : (
          <p className="px-2 text-sm text-ink-muted">
            {filter ? "Keine passenden Chats." : "Noch keine Unterhaltung."}
          </p>
        )}
      </SidebarSection>

      {people.length > 0 ? (
        <SidebarSection title="Personen">
          {people.map((member) => (
            <MemberItem key={member.userId} member={member} onClick={() => openMember(member)} />
          ))}
        </SidebarSection>
      ) : null}

      {guests.length > 0 ? (
        <SidebarSection title="Gäste">
          {guests.map((member) => (
            <MemberItem key={member.userId} member={member} onClick={() => openMember(member)} />
          ))}
        </SidebarSection>
      ) : null}

      {channels.error || members.error || openDirect.error || setFavorite.error ? (
        <p role="alert" className="px-2 text-sm text-danger">
          {(channels.error ?? members.error ?? openDirect.error ?? setFavorite.error)?.message}
        </p>
      ) : null}

      {newChatOpen ? (
        <NewChatDialog
          members={(members.data ?? []).filter((member) => member.userId !== me.user.id)}
          busy={openDirect.isPending}
          error={openDirect.error?.message ?? null}
          onSelect={(member) => openMember(member, true)}
          onClose={() => setNewChatOpen(false)}
        />
      ) : null}
    </div>
  );
}

function MemberItem({ member, onClick }: { member: MemberDTO; onClick: () => void }): JSX.Element {
  return (
    <SidebarItem
      label={member.displayName}
      hint={member.kind === "AGENT" ? `Agent · ${member.email}` : member.email}
      icon={
        <Avatar
          displayName={member.displayName}
          avatarUrl={member.avatarUrl}
          presence={member.presence}
          size="sm"
        />
      }
      active={false}
      onClick={onClick}
    />
  );
}
